import { chunkArray } from '@/helpers/arrayHelpers'
import { debounce } from '@/helpers/debounce'
import { v4 as uuidv4 } from 'uuid'
import constants from '@/helpers/constants'
const MAX_ARRAY_SIZE = 20
const CART_UID_KEY = 'cartUid'

export const GoogleTagManagerService = {
  productsInViewPort: [],
  promotionsInViewPort: [],
  createTemplate (ecommerceEventName, dataKey, { analyticsData = {}, actionField = null, params = {} } = {}) {
    return {
      'ecommerce': {
        'currencyCode': 'UAH',
        ...(ecommerceEventName !== 'impressions'
          ? {
            [ecommerceEventName]: {
              [dataKey]: analyticsData,
              ...(actionField && { 'actionField': actionField })
            }
          }
          : {
            [ecommerceEventName]: analyticsData
          })
      },
      'gtm-ee-event-category': 'Enhanced Ecommerce',
      'gtm-ee-event-non-interaction': `False`,
      ...params
    }
  },
  pushToDatalayer (tmp) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(tmp)
  },
  generalHandler (ecommerceEventName, dataKey, dataObj) {
    return new Promise((resolve, reject) => {
      const chunkedData = chunkArray(MAX_ARRAY_SIZE, dataObj.analyticsData)
      chunkedData.forEach(item => {
        const template = this.createTemplate(ecommerceEventName, dataKey, { ...dataObj, analyticsData: item })
        this.pushToDatalayer(template)
      })
      resolve()
    }).catch(() => {
      return Promise.resolve()
    })
  },
  checkoutEvent ({ analyticsData, actionField, params }) {
    return this.generalHandler('checkout', 'products', { analyticsData, actionField, params })
  },
  purchaseEvent ({ analyticsData, actionField, params }) {
    return this.generalHandler('purchase', 'products', { analyticsData, actionField, params })
  },
  addToCartEvent ({ analyticsData, params }) {
    return this.generalHandler('add', 'products', { analyticsData, params })
  },
  removeFromCartEvent ({ analyticsData, params }) {
    return this.generalHandler('remove', 'products', { analyticsData, params })
  },
  promotionViewEvent ({ analyticsData }) {
    const params = {
      'event': 'promotionView'
    }
    this.promotionsInViewPort = [...this.promotionsInViewPort, ...analyticsData]
    this.promotionViewEventAction({ params })
  },
  promotionViewEventAction: debounce(function ({ params }) {
    this.generalHandler('promoView', 'promotions', {
      analyticsData: this.promotionsInViewPort,
      params
    })
    this.promotionsInViewPort = []
  }, 100),
  promotionClickEvent ({ analyticsData = {}, params = {} } = {}) {
    params = {
      'event': 'promotionClick',
      ...params
    }
    return this.generalHandler('promoClick', 'promotions', { analyticsData, params })
  },
  // then для цього метода не працює :(
  productViewEvent ({ analyticsData }) {
    const params = {
      'event': 'gtm-ee-event-impressions',
      'gtm-ee-event-action': 'Product Impressions',
      'gtm-ee-event-non-interaction': 'True'
    }
    this.productsInViewPort = [...this.productsInViewPort, ...analyticsData]
    this.productViewEventAction({ params })
  },

  productViewEventAction: debounce(function ({ params }) {
    this.generalHandler('impressions', 'products', {
      analyticsData: this.productsInViewPort,
      params
    })
    this.productsInViewPort = []
  }, 100),

  productClickEvent ({ analyticsData, actionField }) {
    const params = {
      'event': `gtm-ee-event-product-click`,
      'gtm-ee-event-action': `Product Clicks`
    }
    return this.generalHandler('click', 'products', { analyticsData, actionField, params })
  },
  productInfoEvent ({ analyticsData, actionField }) {
    const params = {
      'event': 'gtm-ee-event-product-details',
      'gtm-ee-event-action': 'Product Details',
      'gtm-ee-event-non-interaction': `True`
    }
    return this.generalHandler('detail', 'products', {
      analyticsData,
      actionField,
      params
    })
  }
}

export const GoogleEnhancedEcommerce = {
  isEnabled: true,
  saveCollectedData (result) {
    sessionStorage.setItem('analyticsDataReferrer', JSON.stringify(result))
  },
  collectUserAnalyticsData () {
    const defaultReferers = {
      '360.cn': '360.cn',
      'www.alice.com': 'Alice',
      'aliceadsl.fr': 'Alice',
      'www.alltheweb.com': 'Alltheweb',
      'www.altavista.com': 'Altavista',
      'www.aol.com': 'AOL',
      'www.ask.com': 'Ask',
      'search.aol.fr': 'Ask',
      'alicesuche.aol.de': 'Ask',
      'search.auone.jp': 'Auone',
      'isearch.avg.com': 'Avg',
      'search.babylon.com': 'Babylon',
      'www.baidu.com': 'Baidu',
      'biglobe.ne.jp': 'Biglobe',
      'www.bing.com': 'bing',
      'search.centrum.cz': 'Centrum.cz',
      'search.comcast.net': 'Comcast',
      'search.conduit.com': 'Conduit',
      'www.cnn.com/SEARCH': 'CNN',
      'www.daum.net': 'Daum',
      'duckduckgo.com': 'DuckDuckGo',
      'www.ecosia.org': 'Ecosia',
      'www.ekolay.net': 'Ekolay',
      'www.eniro.se': 'Eniro',
      'www.globo.com/busca': 'Globo',
      'go.mail.ru': 'go.mail.ru',
      'www.google.com': 'google',
      'www.google.co.uk': 'google',
      'goo.ne.jp': 'goo.ne',
      'www.haosou.com/s': 'haosou.com',
      'search.incredimail.com': 'Incredimail',
      'www.kvasir.no': 'Kvasir',
      'www.lycos.com': 'Lycos',
      'search.lycos.de': 'Lycos',
      'www.mamma.com': 'Mamma',
      'www.msn.com': 'MSN',
      'money.msn.com': 'MSN',
      'local.msn.com': 'MSN',
      'www.mynet.com': 'MSN',
      'najdi.si': 'Najdi',
      'www.naver.com': 'Naver',
      'search.netscape.com': 'Netscape',
      'szukaj.onet.pl': 'ONET',
      'www.ozu.es': 'Ozu',
      'www.qwant.com': 'Qwant',
      'rakuten.co.jp': 'Rakuten',
      'rambler.ru': 'rambler',
      'search-results.com': 'Search-results',
      'search.smt.docomo.ne.jp': 'search.smt.docomo',
      'sesam.no': 'Sesam',
      'www.seznam.cz': 'Seznam',
      'www.so.com/s': 'So.com',
      'www.sogou.com': 'Sogou',
      'www.startsiden.no/sok': 'Startsiden',
      'www.szukacz.pl': 'Szukacz',
      'buscador.terra.com.br': 'Terra',
      'search.tut.by': 'Tut.by',
      'search.ukr.net': 'Ukr',
      'search.virgilio.it': 'Virgilio',
      'www.voila.fr': 'Voila',
      'www.wp.pl': 'Wirtualna Polska',
      'www.yahoo.com': 'yahoo',
      'yahoo.cn': 'yahoo',
      'm.yahoo.com': 'yahoo',
      'www.yandex.com': 'yandex',
      'yandex.ru': 'yandex',
      'www.yam.com': 'yam'
    }
    const url = new URL(window.location.href)
    const params = Object.fromEntries(url.searchParams.entries())
    const referer = document.referrer.replace(/^https?:\/\//, '').replace(/\/$/, '')
    let result = params
    if (!params.utm_source && !params.utm_medium && params.gclid) {
      result = {
        ...result,
        utm_source: 'google',
        utm_medium: 'cpc_server'
      }
      this.saveCollectedData(result)
    } else if (!params.utm_source && !params.utm_medium && params.fbclid && referer.includes('facebook')) {
      result = {
        ...result,
        utm_source: 'facebook',
        utm_medium: 'MP_server'
      }
      this.saveCollectedData(result)
    } else if (!params.utm_source && !params.utm_medium && params.fbclid && referer.includes('instagram')) {
      result = {
        ...result,
        utm_source: 'instagram',
        utm_medium: 'MP_server'
      }
      this.saveCollectedData(result)
    } else if (!params.utm_source && !params.utm_medium && params.fbclid && !referer.includes('instagram')) {
      result = {
        ...result,
        utm_source: 'facebook_instagram',
        utm_medium: 'MP_server'
      }
      this.saveCollectedData(result)
    } else if (defaultReferers[referer] && !params.utm_source && !params.utm_medium && !params.fbclid && !params.gclid) {
      result = {
        ...result,
        utm_source: defaultReferers[referer],
        utm_medium: 'organic'
      }
      this.saveCollectedData(result)
    } else if (referer && !defaultReferers[referer] && !params.utm_source && !params.utm_medium && (!params.fbclid || !params.gclid)) {
      result = {
        ...result,
        utm_source: referer,
        utm_medium: 'referal'
      }
      this.saveCollectedData(result)
    } else if ((!params.fbclid || !params.gclid) && (params.utm_source || params.utm_medium) && (params.utm_content || params.utm_term || params.utm_campaign)) {
      this.saveCollectedData(result)
    } else if (!params.utm_source && !params.utm_medium && !referer && (!params.fbclid || !params.gclid)) {
      result = {
        ...result,
        utm_source: '(direct)',
        utm_medium: '(none)_server'
      }
      this.saveCollectedData(result)
    }
  },
  getGoogleEnhancedEcommerceData (currData) {
    let itemData = {
      'item_name': currData.name_uk,
      'item_id': currData.id, // https://imgur.com/TtuDO5O передаємо ідентифікатор як у фіді Google Merchant
      'price': +currData.price, // https://imgur.com/juehVh8
      'discount': +(parseFloat(currData.discount).toFixed(2)) || 0, // https://imgur.com/vtrNxWK
      'currency': 'UAH', // Валюта
      'item_brand': 'Dnipro-M', // Бренд товару
      'quantity': currData.quantity || 1 // Кількість одиниць товару
    }
    if (currData.categories) {
      currData.categories.forEach((el, index) => {
        let name = 'item_category'
        if (index > 0) {
          name = 'item_category' + parseInt(index + 1)
        }
        itemData[name] = el.name_uk
      })
    }
    if (currData.option && currData.option.value) {
      itemData.item_variant = currData.option.value
    }
    const promoBannerData = this.getPromoBannerData(currData.id)
    if (Object.keys(promoBannerData).length) {
      itemData.promotion_name = promoBannerData.promotion_name
      itemData.promotion_id = promoBannerData.promotion_id.toString()
      itemData.creative_slot = promoBannerData.creative_slot
    }
    itemData = { ...itemData, ...this.getSavedListData(currData.id) }
    return itemData
  },
  getPromoBannerData (code) {
    const savedBanners = JSON.parse(localStorage.getItem('clickedBanners')) || []
    const bannerPromoItems = JSON.parse(localStorage.getItem('bannerPromoItems')) || {}
    for (let key in bannerPromoItems) {
      const promoItem = bannerPromoItems[key].find(el => el.toString() === code.toString())
      if (promoItem) {
        return savedBanners.find(el => el.promotion_id.toString() === key.toString()) || {}
      }
    }
    return {}
  },
  getSavedListData (itemId) {
    let savedLists = JSON.parse(localStorage.getItem('savedLists')) || []
    let result = {}
    savedLists.forEach(el => {
      const hasItemId = el.items.find(el => el === itemId)
      if (hasItemId) {
        result = {
          item_list_id: el.item_list_id,
          item_list_name: el.item_list_name
        }
      }
    })
    return result
  },
  setList (list) {
    let savedLists = JSON.parse(localStorage.getItem('savedLists')) || []
    let sameList = savedLists.find(el => el.item_list_id === list.item_list_id)
    const sameListIndex = savedLists.findIndex(el => el.id === list.item_list_id)
    // чтоб отправлять только ПОСЛЕДНИЙ список в котором был товар, мы убираем id товара с всех других списков перед добавлением
    savedLists.forEach(el => {
      list.items.forEach(listId => {
        const oldIdInOldListIndex = el.items.findIndex(id => id === listId)
        if (oldIdInOldListIndex > -1) {
          el.items.splice(oldIdInOldListIndex, 1)
        }
      })
    })
    if (sameList) {
      sameList.items = [...sameList.items, ...list.items]
      savedLists[sameListIndex] = sameList
    } else {
      savedLists.push(list)
    }
    localStorage.setItem('savedLists', JSON.stringify(savedLists))
  },
  pushToDatalayer (tmp) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(tmp)
    // posthog.capture(tmp.event, tmp)
  },
  featureFlags () {
    const features = window.config?.features || {}
    for (let key in features) {
      this.pushToDatalayer({
        event: 'experience_impression',
        exp_variant_string: `PostHog-${key}-${features[key]}`
      })
    }
  },
  emptySearchResults (q) {
    this.pushToDatalayer({
      event: 'search_no_results',
      search_term: q
    })
  },
  emailSubscription () {
    this.pushToDatalayer({
      event: 'email_subscription'
    })
  },
  viewPromotion (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'view_promotion',
      ecommerce: data
    })
  },
  selectPromotion (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'select_promotion',
      ecommerce: data
    })
  },
  selectItem (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'select_item',
      ecommerce: data
    })
  },
  viewItem (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'view_item',
      ecommerce: data
    })
  },
  viewItemList ({ analyticsData, listId, listName }) {
    const data = analyticsData.map(el => {
      return {
        ...el,
        item_list_id: listId || 'відсутній',
        item_list_name: listName
      }
    })
    const result = {
      event: 'view_item_list',
      ecommerce: {
        items: data
      }
    }
    this.setList({
      item_list_id: listId,
      item_list_name: listName,
      items: analyticsData.map(el => el.item_id)
    })
    this.pushToDatalayer(result)
  },
  addToCart (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_to_cart',
      ecommerce: data
    })
  },
  removeFromCart (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'remove_from_cart',
      ecommerce: data
    })
  },
  addToWishlist (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_to_wishlist',
      ecommerce: data
    })
  },
  beginCheckout (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'begin_checkout',
      ecommerce: data
    })
  },
  contactInformation (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'contact_information',
      ecommerce: data
    })
  },
  addShippingInfo (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_shipping_info',
      ecommerce: data
    })
  },
  addPaymentInfo (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'add_payment_info',
      ecommerce: data
    })
  },
  purchase (data) {
    this.pushToDatalayer({ ecommerce: null })
    this.pushToDatalayer({
      event: 'purchase',
      ecommerce: data
    })
  }
}

export const EsputnikService = {
  createGeneralInfoTemplate (userData = {}) {
    return {
      'user_email': userData.email ?? '',
      'user_phone': userData.phone ?? '',
      'user_es_contact_id': this.getEsUserId()
    }
  },
  createCustomerDataTemplate ({ userData = {}, selectedUserLocation = {} }) {
    return {
      'user_email': userData.email ?? '',
      'user_name': userData.name ?? '',
      'user_client_id': userData.user_id ?? '',
      'user_phone': userData.phone ?? '',
      'user_city': selectedUserLocation.name_ru ?? ''
    }
  },
  createCartProductsTemplate ({ products }) {
    return products.map(item => {
      return {
        'productKey': item.id ?? '',
        'price': item.price ?? '',
        'quantity': item.quantity ?? '',
        'currency': 'UAH'
      }
    })
  },
  createProductInfoTemplate ({ product }) {
    return {
      'productKey': product.productKey ?? '',
      'price': product.price ?? '',
      'isInStock': product.isInStock ? '1' : '0'
    }
  },
  createProductImpressionsTemplate ({ products }) {
    return products.map(item => {
      return {
        'product_id': item.id,
        'container_type': item.container_type
      }
    })
  },
  eventWrapper ({ mainEventName = 'sendEvent', eventName, data = {} }) {
    if (!window.config?.login_as_user) {
      const parsedDataFromLocalStorage = JSON.parse(localStorage.getItem(constants.USER_DATA_KEY) ?? null) ?? {}
      let generalInfo = {
        'GeneralInfo': this.createGeneralInfoTemplate(parsedDataFromLocalStorage)
      }
      if (Object.keys(data).length) {
        if (data.hasOwnProperty('GeneralInfo')) {
          generalInfo.GeneralInfo = {
            ...data.GeneralInfo,
            ...generalInfo.GeneralInfo
          }
          delete data.GeneralInfo
        }
        data = {
          ...generalInfo,
          ...data
        }
      } else {
        data = generalInfo
      }

      window.eS(mainEventName, eventName, data)
    }
  },
  sendProductPageInfo ({ product, blockId }) {
    const data = {
      'ProductPage': this.createProductInfoTemplate({ product })
    }

    if (blockId) {
      data['GeneralInfo'] = {}
      data['GeneralInfo']['s_location'] = blockId
    }

    this.eventWrapper({
      eventName: 'ProductPage',
      data
    })
  },
  sendCategoryPageInfo ({ categoryName }) {
    this.eventWrapper({
      eventName: 'CategoryPage',
      data: {
        'CategoryPage': {
          'categoryKey': categoryName
        }
      }
    })
  },
  sendAddToWishlist ({ productKey, price, isInStock }) {
    this.eventWrapper({
      eventName: 'AddToWishlist',
      data: {
        'AddToWishlist': {
          productKey: productKey.toString(),
          price: price.toString(),
          isInStock: isInStock ? 1 : 0
        }
      }
    })
  },
  sendErrorPageInfo () {
    this.eventWrapper({ eventName: 'NotFound' })
  },
  sendMainPageInfo () {
    this.eventWrapper({ eventName: 'MainPage' })
  },
  sendStatusCartInfo ({ products }) {
    const data = {
      'StatusCart': this.createCartProductsTemplate({ products }),
      'GUID': this.generateCartUid()
    }

    this.eventWrapper({
      eventName: 'StatusCart',
      data
    })
  },
  sendPurchasedItemsInfo ({ products, orderNumber, isCheckout = true, tags = null }) {
    const data = {
      'PurchasedItems': this.createCartProductsTemplate({ products }),
      'OrderNumber': orderNumber
    }

    if (isCheckout) data['GUID'] = this.getCurrentCartUid()

    if (tags) data['Tags'] = tags

    this.eventWrapper({
      eventName: 'PurchasedItems',
      data
    })
  },
  sendCustomerDataInfo ({ userData, selectedUserLocation }) {
    this.eventWrapper({
      eventName: 'CustomerData',
      userData,
      data: {
        'CustomerData': this.createCustomerDataTemplate({ userData, selectedUserLocation })
      }
    })
  },
  sendProductImpressions ({ products }) {
    this.eventWrapper({
      eventName: 'ProductImpressions',
      data: {
        'ProductImpression': this.createProductImpressionsTemplate({ products })
      }
    })
  },
  generateCartUid () {
    const uid = uuidv4()

    localStorage.setItem(CART_UID_KEY, uid)

    return uid
  },
  getCurrentCartUid () {
    return localStorage.getItem(CART_UID_KEY) ?? ''
  },
  getEsUserId () {
    return localStorage.getItem('esWebPushContactId') ?? ''
  },
  getRecommendations (params) {
    return new Promise((resolve, reject) => {
      window.eS('getRecommendations', params,
        function (error, products) {
          if (error) reject(error)
          resolve(products)
        })
    })
  },
  triggerEsEvent (eventName, params) {
    window.eS(eventName, params)
  }
}
